// ---------------------------------------------------------------------------
// The Assessment setup page's view model (NDI-239 v2)
//
// Pure, so it tests without a page. The server decides which step is done,
// open or blocked (`setup.steps`, spec §5) — nothing here re-derives that.
// This module only reads it (a word, or an object with a reason), picks the
// step to open, says which dots can be clicked, and shapes what the cards
// show: the status word, "3 of 3 scenarios match the Excel" and its table
// (numbers German, with their unit, as the customer's PDF prints them), the
// placeholder chips, and the client-side upload checks.
// ---------------------------------------------------------------------------

import type { StatusLook } from '../campaign-status.js';
import type { NumberFormat, ScriptVerification, SetupOutput, TemplatePlaceholder } from './api.js';

export type StepKey = 'calculator' | 'fields' | 'check' | 'template' | 'example';
export type StepState = 'done' | 'todo' | 'blocked';
export interface StepInfo {
  state: StepState;
  /** German, from the server — why a step is blocked. */
  reason?: string;
}
export type Steps = Record<StepKey | 'approved', StepInfo>;

/** The five steps, in order, as the stepper names them. */
export const SETUP_STEPS: readonly { key: StepKey; label: string }[] = [
  { key: 'calculator', label: 'Calculator' },
  { key: 'fields', label: 'Inputs' },
  { key: 'check', label: 'Check' },
  { key: 'template', label: 'Template' },
  { key: 'example', label: 'Example' },
];

const STATES = new Set<string>(['done', 'todo', 'blocked']);

function readStep(raw: unknown): StepInfo {
  if (typeof raw === 'string') return { state: STATES.has(raw) ? (raw as StepState) : 'todo' };
  if (raw && typeof raw === 'object') {
    const r = raw as { state?: unknown; status?: unknown; reason?: unknown };
    const word = typeof r.state === 'string' ? r.state : typeof r.status === 'string' ? r.status : '';
    const state: StepState = STATES.has(word) ? (word as StepState) : 'todo';
    return typeof r.reason === 'string' && r.reason.trim() ? { state, reason: r.reason.trim() } : { state };
  }
  return { state: 'todo' };
}

/** The server's step states; a missing or unknown one is todo. */
export function readSteps(raw: unknown): Steps {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out = {} as Steps;
  for (const key of [...SETUP_STEPS.map(s => s.key), 'approved'] as const) out[key] = readStep(src[key]);
  return out;
}

/** The step a setup opens on: the first that is not done; Example when all are. */
export function firstOpenStep(steps: Steps): StepKey {
  return SETUP_STEPS.find(s => steps[s.key].state !== 'done')?.key ?? 'example';
}

export interface StepperItem {
  key: string;
  label: string;
  number: number;
  look: 'done' | 'current' | 'todo' | 'blocked';
  /** Clickable: every step the server did not block, and the one shown. */
  reachable: boolean;
  reason?: string;
}

/** The stepper's dots for the step shown (`current`). */
export function stepperItems(steps: Steps, current: StepKey): StepperItem[] {
  return SETUP_STEPS.map((s, i) => {
    const { state, reason } = steps[s.key];
    const look: StepperItem['look'] = s.key === current ? 'current' : state;
    return {
      key: s.key,
      label: s.label,
      number: i + 1,
      look,
      reachable: s.key === current || state !== 'blocked',
      ...(reason ? { reason } : {}),
    };
  });
}

const APPROVAL: Record<string, StatusLook> = {
  draft: { tone: 'neutral', label: 'Draft' },
  pending: { tone: 'caution', label: 'Needs approval' },
  approved: { tone: 'positive', label: 'Approved' },
  'changes-requested': { tone: 'caution', label: 'Changes requested' },
};

/** The setup's status as a dot and a word. */
export function approvalLook(status: string | undefined): StatusLook {
  return (status && Object.hasOwn(APPROVAL, status) ? APPROVAL[status] : undefined) ?? APPROVAL.draft!;
}

/** "3 of 3 scenarios match the Excel" — or how many did; null before any check. */
export function scenarioSummary(
  verification: ScriptVerification | undefined,
): { passed: boolean; matched: number; total: number; text: string } | null {
  if (!verification || !Array.isArray(verification.scenarios)) return null;
  const total = verification.scenarios.length;
  const matched = verification.scenarios.filter(s => s.equal).length;
  const passed = !!verification.passed && total > 0 && matched === total;
  return { passed, matched, total, text: `${matched} of ${total} scenarios match the Excel` };
}

/** How a value is printed: the setup's format and unit (api.ts SetupInput / SetupOutput). */
export interface ValueLook {
  format?: NumberFormat;
  unit?: string;
}

/** The sign a format prints by itself, as the server's PDF does (render/format.ts). */
const FORMAT_SIGN: Partial<Record<NumberFormat, string>> = { eur: '€', eurCompact: '€', percent: '%' };

/** The unit a value is shown with: the format's own sign, else the setup's unit; '' when none. */
export function unitOf(look: ValueLook | undefined): string {
  return (look?.format && FORMAT_SIGN[look.format]) || look?.unit?.trim() || '';
}

/**
 * Numbers as the customer reads them — German (1.234,5), at most `digits` decimals — with the unit the PDF prints
 * ("146.016 €", "80 %": a percent is held as a fraction, 0.8, as on the server); text as it is; nothing as a dash.
 */
export function formatNumber(value: unknown, look?: ValueLook, digits = 2): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const n = look?.format === 'percent' ? value * 100 : value;
    const text = n.toLocaleString('de-DE', { maximumFractionDigits: digits });
    const unit = unitOf(look);
    return unit ? `${text} ${unit}` : text;
  }
  if (typeof value === 'string' && value) return value;
  return '–';
}

/**
 * A label without the unit in brackets that its value already shows: "Ersparnis pro Jahr (€)" over "146.016 €" says
 * the euro twice. Only a trailing "(€)", "(%)" or "(<unit>)" equal to the value's unit goes.
 */
export function labelWithoutUnit(label: string, unit: string): string {
  if (!unit) return label;
  const m = /\s*\(([^()]+)\)\s*$/.exec(label);
  if (!m) return label;
  const inner = m[1]!.trim().toLowerCase();
  const same =
    inner === unit.toLowerCase() || (unit === '€' && inner === 'eur') || (unit === '%' && inner === 'prozent');
  return same ? label.slice(0, m.index).trim() || label : label;
}

/** A label whose bracket stays on the word before it: "Stundensatz Lehrkraft (€)" never wraps to a lone "(€)". */
export function labelKeepingUnit(label: string): string {
  return label.replace(/ \(/g, ' (');
}

export interface ScenarioRow {
  scenario: number;
  key: string;
  label: string;
  excel: string;
  script: string;
  equal: boolean;
}

/**
 * One row per scenario and result: what Excel computed, what the script did — German, with the unit, the label
 * without it. A row that differs keeps four decimals, so the difference shows.
 */
export function scenarioRows(verification: ScriptVerification | undefined, outputs: SetupOutput[]): ScenarioRow[] {
  if (!verification || !Array.isArray(verification.scenarios)) return [];
  const byKey = new Map((outputs ?? []).map(o => [o.key, o]));
  return verification.scenarios.flatMap((s, i) => {
    const keys = [...new Set([...Object.keys(s.excel ?? {}), ...Object.keys(s.script ?? {})])];
    return keys.map(key => {
      const output = byKey.get(key);
      const excel = s.excel?.[key];
      const script = s.script?.[key];
      const equal = s.equal && excel === script;
      const digits = equal ? 2 : 4;
      return {
        scenario: i + 1,
        key,
        label: labelWithoutUnit(output?.label ?? key, unitOf(output)),
        excel: formatNumber(excel, output, digits),
        script: formatNumber(script, output, digits),
        equal,
      };
    });
  });
}

/**
 * The rows that do not match — all a failed check needs to show: in a scenario that failed, the results whose values
 * differ (all of them when the server failed it although no value differs, so it is never silent).
 */
export function mismatchedRows(verification: ScriptVerification | undefined, outputs: SetupOutput[]): ScenarioRow[] {
  const rows = scenarioRows(verification, outputs);
  return (verification?.scenarios ?? []).flatMap((s, i) => {
    const mine = rows.filter(r => r.scenario === i + 1);
    const differ = mine.filter(r => s.excel?.[r.key] !== s.script?.[r.key]);
    return s.equal || differ.length ? differ : mine;
  });
}

/**
 * The check's German reasons without those about a scenario the mismatch table already shows: the server writes one
 * „Szenario n: … weicht ab – Excel …, Skript …“ per difference, which is what that table's row says. Any other reason
 * (none compared, a scenario that failed to run, a scenario with no row) stays.
 */
export function reasonsBesideTable(reasons: readonly string[], rows: readonly Pick<ScenarioRow, 'scenario'>[]): string[] {
  const inTable = new Set(rows.map(r => r.scenario));
  return reasons.filter(r => {
    const n = /^Szenario (\d+):/.exec(r)?.[1];
    return n === undefined || !inTable.has(Number(n));
  });
}

export interface ScenarioTable {
  /** Scenario numbers, one column each. */
  scenarios: number[];
  /** One row per result: its label, and the Excel value in each scenario. */
  rows: { key: string; label: string; values: string[] }[];
}

/** A passed check as a small table: a row per result, a column per scenario, one value per cell (they matched). */
export function scenarioTable(verification: ScriptVerification | undefined, outputs: SetupOutput[]): ScenarioTable {
  const flat = scenarioRows(verification, outputs);
  const scenarios = [...new Set(flat.map(r => r.scenario))];
  const keys = [...new Set(flat.map(r => r.key))];
  return {
    scenarios,
    rows: keys.map(key => ({
      key,
      label: flat.find(r => r.key === key)?.label ?? key,
      values: scenarios.map(n => flat.find(r => r.key === key && r.scenario === n)?.excel ?? '–'),
    })),
  };
}

export interface ValueRefOption {
  /** What the mapping stores: 'company' | 'contact' | 'date' | 'input:<key>' | 'output:<key>'. */
  ref: string;
  label: string;
  /** The placeholder name that picks it by itself — what click-to-copy puts on the clipboard as {{name}}. */
  name: string;
}

/**
 * Everything a placeholder can show — as the server accepts it: company, contact, date, every input, and every KEPT
 * output (an output that is not kept is not computed at run time, and the mapping refuses it).
 */
export function valueRefOptions(setup: {
  inputs?: { key: string; label: string }[];
  outputs?: { key: string; label: string; keep?: boolean }[];
}): ValueRefOption[] {
  return [
    { ref: 'company', label: 'Company', name: 'company' },
    { ref: 'contact', label: 'Contact', name: 'contact' },
    { ref: 'date', label: 'Date', name: 'date' },
    ...(setup.inputs ?? []).map(i => ({ ref: `input:${i.key}`, label: i.label || i.key, name: i.key })),
    ...(setup.outputs ?? [])
      .filter(o => o.keep !== false)
      .map(o => ({ ref: `output:${o.key}`, label: o.label || o.key, name: o.key })),
  ];
}

export interface PlaceholderChip {
  /** As first written in the template; the mapping's key is its lower case. */
  name: string;
  state: 'mapped' | 'unmapped' | 'unfillable';
  ref?: string;
  valueLabel?: string;
  /** German, from the server — why the engine cannot fill it. */
  reason?: string;
  /** Filled by its name alone (company, contact, date, a key) — the server needs no mapping entry. */
  auto?: true;
  /** A form field of the PDF rather than a {{placeholder}}. */
  field?: true;
}

/**
 * The template's names, each once (the server lists every occurrence of a placeholder), then its form fields: each
 * with what it shows — its mapping entry, else what the server fills by the name alone — or that it needs a choice,
 * or cannot be filled.
 */
export function placeholderChips(
  placeholders: TemplatePlaceholder[] | undefined,
  mapping: Record<string, string> | undefined,
  options: ValueRefOption[],
  formFields: string[] = [],
): PlaceholderChip[] {
  const byRef = new Map(options.map(o => [o.ref, o]));
  const byName = new Map(options.map(o => [o.name.toLowerCase(), o]));
  const seen = new Map<string, { name: string; fillable: boolean; reason?: string; field: boolean }>();
  for (const p of placeholders ?? []) {
    const key = p.name.trim().toLowerCase();
    const before = seen.get(key);
    if (!before)
      seen.set(key, { name: p.name, fillable: p.fillable, field: false, ...(p.reason ? { reason: p.reason } : {}) });
    else if (before.fillable && !p.fillable)
      seen.set(key, { ...before, fillable: false, ...(p.reason ? { reason: p.reason } : {}) });
  }
  for (const f of formFields) {
    const key = f.trim().toLowerCase();
    if (key && !seen.has(key)) seen.set(key, { name: f, fillable: true, field: true });
  }
  return [...seen].map(([key, p]) => {
    const kind = p.field ? { field: true as const } : {};
    if (!p.fillable) return { name: p.name, state: 'unfillable', ...(p.reason ? { reason: p.reason } : {}), ...kind };
    const chosen = mapping && Object.hasOwn(mapping, key) ? mapping[key] : undefined;
    const option = chosen ? byRef.get(chosen) : undefined;
    if (option) return { name: p.name, state: 'mapped', ref: option.ref, valueLabel: option.label, ...kind };
    // No entry: company, contact, date and every input and kept output key fill themselves (server: directRef).
    const direct = chosen ? undefined : byName.get(key);
    if (direct)
      return { name: p.name, state: 'mapped', ref: direct.ref, valueLabel: direct.label, auto: true, ...kind };
    return { name: p.name, state: 'unmapped', ...kind };
  });
}

/**
 * The name alone fills it (server: directRef — company, contact, date, an input or kept output key): removing its
 * mapping entry changes nothing, so the page offers no "Not set" for it (R35).
 */
export function fillsByName(name: string, options: ValueRefOption[]): boolean {
  const key = name.trim().toLowerCase();
  return options.some(o => o.name.toLowerCase() === key);
}

/** Why a plausible range cannot be saved, in a few words; null when it can (the server refuses the same). */
export function rangeProblem(min: unknown, max: unknown): string | null {
  const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
  if (!finite(min) || !finite(max)) return 'Enter both numbers.';
  if (min > max) return 'Minimum is above maximum.';
  return null;
}

const MB = 1024 * 1024;
/** The server caps the same (spec §2/§4); refusing here saves a 10 MB round trip. */
export const UPLOAD_LIMITS = { xlsx: 5 * MB, pdf: 10 * MB } as const;

/** Why a chosen file cannot be uploaded, in one short sentence; null when it can. */
export function checkUpload(file: { name: string; size: number }, kind: 'xlsx' | 'pdf'): string | null {
  if (!file.name.toLowerCase().endsWith(`.${kind}`))
    return kind === 'xlsx' ? 'Please choose an .xlsx file.' : 'Please choose a .pdf file.';
  if (file.size <= 0) return 'The file is empty.';
  if (file.size > UPLOAD_LIMITS[kind]) return `The file is larger than ${UPLOAD_LIMITS[kind] / MB} MB.`;
  return null;
}

/** Bytes as base64, in chunks so a 10 MB file does not overflow the argument list. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}
